import React from 'react';

/**
 * Italicize the brand wordmark wherever it appears in plain-text copy.
 *
 * The wordmark is the lowercase "enough" *with its trailing period* — "enough.".
 * The period is what distinguishes the brand from the ordinary English word, so we
 * only emphasize "enough" when a period immediately follows it:
 *   - "you've been with enough. for 2 months"  → italicize "enough."
 *   - "you have enough time to decide"         → leave alone
 *
 * Headlines and subheadlines come from data and templates as plain strings, not
 * HTML, so we can't embed `<i>`/`<em>` markup in the text itself. This splits the
 * string and wraps each "enough." (period included) in an italic element,
 * returning React nodes that drop into any text slot. Casing is preserved and
 * substrings like "enoughness" are left alone.
 *
 * Returns the original string untouched when there's no wordmark, so it's safe to
 * wrap any text without adding DOM nodes unnecessarily.
 */
const BRAND_RE = /\benough\./gi;

export function emphasizeBrand(text: string | null | undefined): React.ReactNode {
  if (!text) return text ?? null;

  const nodes: React.ReactNode[] = [];
  let last = 0;
  let key = 0;

  for (const match of text.matchAll(BRAND_RE)) {
    const start = match.index ?? 0;
    if (start > last) nodes.push(text.slice(last, start));
    // Inline style, not just the <em> default: parents (e.g. the insight Headline)
    // set fontStyle:'normal' inline, which the <em> would otherwise inherit.
    nodes.push(
      <em key={key++} style={{ fontStyle: 'italic' }}>
        {match[0]}
      </em>,
    );
    last = start + match[0].length;
  }

  // No brand mention — hand back the plain string (no extra nodes).
  if (last === 0) return text;
  if (last < text.length) nodes.push(text.slice(last));
  return <>{nodes}</>;
}
